import React from "react";

type UploadProgressProps = {
    fileName: string;
    fileSize: string;
    progress: number; // 0-100
    status?: "uploading" | "processing" | "complete";
    className?: string;
};

const UploadProgress = ({
    fileName,
    fileSize,
    progress,
    status = "processing",
    className = "",
}: UploadProgressProps) => {
    const getStatusText = () => {
        switch (status) {
            case "uploading":
                return "Uploading...";
            case "processing":
                return "being processed";
            case "complete":
                return "Complete";
            default:
                return "being processed";
        }
    };

    return (
        <div className={`w-full ${className}`}>
            {/* Outer dashed border container - matching FileUpload design */}
            <div
                className="relative border-2 border-dashed rounded-lg p-24 min-h-[600px] flex items-center justify-center bg-white"
                style={{ borderColor: "#DAB0FB" }}
            >
                {/* Progress card */}
                <div 
                    className="w-full max-w-[786px] bg-white p-4 flex items-center gap-4"
                    style={{
                        height: '96px',
                        borderRadius: '24px',
                        border: '1px solid #DFDFE0',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    {/* Custom Google Sheets/Excel icon */}
                    <div className="flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="51" height="51" viewBox="0 0 51 51" fill="none">
                            <path d="M11.5501 2.1001C9.81045 2.1001 8.40015 3.5104 8.40015 5.2501V45.1501C8.40015 46.8898 9.81045 48.3001 11.5501 48.3001H38.8501C40.5898 48.3001 42.0001 46.8898 42.0001 45.1501V14.7001L29.4001 2.1001H11.5501Z" fill="#20A464"/>
                            <path d="M11.5501 48.2998C9.81045 48.2998 8.40015 46.8895 8.40015 45.1498V44.6248C8.40015 46.3645 9.81045 47.7748 11.5501 47.7748H38.8501C40.5898 47.7748 42.0001 46.3645 42.0001 44.6248V45.1498C42.0001 46.8895 40.5898 48.2998 38.8501 48.2998H11.5501Z" fill="#149456"/>
                            <path d="M11.5501 2.1001C9.81045 2.1001 8.40015 3.5104 8.40015 5.2501V5.7751C8.40015 4.0354 9.81045 2.6251 11.5501 2.6251H29.9251L29.4001 2.1001H11.5501Z" fill="#38AE74"/>
                            <path d="M42.0003 25.4628V14.7003L40.1628 12.8628H31.3015C30.5999 12.8628 30.2486 13.711 30.7447 14.2071L42.0003 25.4628Z" fill="url(#paint0_linear_801_9619)"/>
                            <path d="M29.4001 2.1001L42.0001 14.7001H32.5501C30.8104 14.7001 29.4001 13.2898 29.4001 11.5501V2.1001Z" fill="#8ED1B1"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M33.5998 24.6753V39.9003H16.7998V24.6753H33.5998ZM24.1498 26.7753H18.8998V29.4003H24.1498V26.7753ZM26.2498 29.4003V26.7753H31.4998V29.4003H26.2498ZM24.1498 30.9753H18.8998V33.6003H24.1498V30.9753ZM26.2498 33.6003V30.9753H31.4998V33.6003H26.2498ZM24.1498 35.1753H18.8998V37.8003H24.1498V35.1753ZM26.2498 37.8003V35.1753H31.4998V37.8003H26.2498Z" fill="white"/>
                            <defs>
                                <linearGradient id="paint0_linear_801_9619" x1="36.2564" y1="25.4628" x2="36.2564" y2="12.6003" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#20A464"/>
                                    <stop offset="1" stopColor="#207E55"/>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>

                    {/* File info and progress */}
                    <div className="flex-1 flex flex-col gap-2">
                        {/* File name and status */}
                        <div>
                            <p className="text-charcoal font-semibold text-md leading-tight">
                                '{fileName}' {getStatusText()}
                            </p>
                            <p className="text-muted text-sm">{fileSize}</p>
                        </div>

                        {/* Progress bar */}
                        <div className="relative w-full h-2 bg-cloud rounded-full overflow-hidden">
                            <div
                                className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all duration-300"
                                style={{ width: `${Math.round(progress)}%` }}
                            />
                        </div>
                    </div>

                    {/* Percentage */}
                    <div className="flex-shrink-0">
                        <p className="text-semibold text-muted">
                            {Math.round(progress)}%
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadProgress;

